import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import {
  CreateTaskTrackerBoardRequest,
  GetTaskTrackerBoardParams,
  GetTaskTrackerBoardRefreshItemsParams,
  MoveTaskTrackerBoardsItem,
  TaskTrackerBoard,
  TaskTrackerBoardAccessSettings,
  TaskTrackerBoardDetails,
  TaskTrackerBoardRefreshItemsResult,
  UpdateTaskTrackerBoardAccessRequest,
  UpdateTaskTrackerBoardRequest,
} from '../store/taskTracker.types'
import {
  mapBoardAccessFromApi,
  mapBoardCardsFromApi,
  mapBoardDetailsFromApi,
  mapBoardFromApi,
  sortBoardsByPosition,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  ApiBoard,
  ApiBoardAccessResponse,
  ApiBoardDetails,
  ApiBoardRefreshItemsResponse,
  ApiBoardsMoveResponse,
  ApiDeleteBoardResponse,
} from './taskTracker.api.types'
import {
  DEFAULT_TASK_TRACKER_BOARD_CARDS_LIMIT,
  DEFAULT_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
  MAX_TASK_TRACKER_BOARD_CARDS_LIMIT,
  MAX_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
} from '../lib/taskTrackerBoard.constants'
import { appendTaskTrackerStickerFiltersToSearchParams, appendTaskTrackerBoardCompletedStatusToSearchParams } from '../lib/taskTrackerBoard.stickerFilters.utils'

const normalizePositiveIdList = (ids?: number[]) =>
  [...new Set((ids ?? []).map(Number).filter((id) => Number.isFinite(id) && id > 0))]

const normalizeRefreshLimit = (limit?: number) => {
  const raw = limit ?? DEFAULT_TASK_TRACKER_COLUMN_ITEMS_LIMIT
  return Math.min(
    Math.max(1, Math.floor(raw)),
    MAX_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
  )
}

const normalizeRefreshCursor = (value: unknown): string | null => {
  if (value == null) return null
  const asString = String(value).trim()
  return asString.length > 0 ? asString : null
}

const buildBoardRefreshItemsQuery = (
  params: GetTaskTrackerBoardRefreshItemsParams,
) => {
  const searchParams = new URLSearchParams()
  searchParams.set('board_id', String(params.boardId))
  searchParams.set('limit', String(normalizeRefreshLimit(params.limit)))

  const sortBy = params.sortBy?.trim()
  if (sortBy) {
    searchParams.set('sort_by', sortBy)
    searchParams.set('sort_order', params.sortOrder ?? 'asc')
  }

  if (params.hasDeadline === true) {
    searchParams.set('has_deadline', 'true')
  } else if (params.hasDeadline === false) {
    searchParams.set('has_deadline', 'false')
  }

  appendTaskTrackerBoardCompletedStatusToSearchParams(
    searchParams,
    params.isCompleted,
  )
  appendTaskTrackerStickerFiltersToSearchParams(
    searchParams,
    params.stickerFilters,
  )

  const summaryIds = normalizePositiveIdList(params.summaryIds)
  if (summaryIds.length > 0) {
    searchParams.set('summary_ids', summaryIds.join(','))
  }

  const columnSettings = (params.columnSettings ?? []).filter(
    (item) => Number.isFinite(item.columnId) && item.columnId > 0,
  )

  if (columnSettings.length > 0) {
    columnSettings.forEach((setting, index) => {
      searchParams.set(
        `column_settings[${index}][column_id]`,
        String(setting.columnId),
      )
      if (setting.limit != null) {
        searchParams.set(
          `column_settings[${index}][limit]`,
          String(normalizeRefreshLimit(setting.limit)),
        )
      }
      const columnSortBy = setting.sortBy?.trim()
      if (columnSortBy) {
        searchParams.set(`column_settings[${index}][sort_by]`, columnSortBy)
        searchParams.set(
          `column_settings[${index}][sort_order]`,
          setting.sortOrder ?? 'asc',
        )
      }
    })
  } else {
    const columnIds = normalizePositiveIdList(params.columnIds)
    if (columnIds.length > 0) {
      searchParams.set('column_ids', columnIds.join(','))
    }
  }

  return `?${searchParams.toString()}`
}

const buildBoardIndexQuery = (
  boardId: number,
  params?: GetTaskTrackerBoardParams,
) => {
  const rawLimit = params?.limit ?? DEFAULT_TASK_TRACKER_BOARD_CARDS_LIMIT
  const limit = Math.min(
    Math.max(1, Math.floor(rawLimit)),
    MAX_TASK_TRACKER_BOARD_CARDS_LIMIT,
  )

  const searchParams = new URLSearchParams()
  searchParams.set('board_id', String(boardId))
  searchParams.set('limit', String(limit))
  appendTaskTrackerStickerFiltersToSearchParams(
    searchParams,
    params?.stickerFilters,
  )
  appendTaskTrackerBoardCompletedStatusToSearchParams(
    searchParams,
    params?.isCompleted,
  )

  return `?${searchParams.toString()}`
}

export const taskTrackerBoardsApi = {
  async getBoard(
    boardId: number,
    params?: GetTaskTrackerBoardParams,
  ): Promise<TaskTrackerBoardDetails> {
    const res = await API.get<ResponseWrapper<ApiBoardDetails>>(
      `task-tracker-boards/index${buildBoardIndexQuery(boardId, params)}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить доску'))
    }
    if (!res.data?.data?.board) {
      throw new Error('Не удалось загрузить доску')
    }
    return mapBoardDetailsFromApi(res.data.data)
  },

  async getBoards(projectId: number): Promise<TaskTrackerBoard[]> {
    const res = await API.get<ResponseWrapper<ApiBoard[]>>(
      `task-tracker-boards/index${getParams({ project_id: projectId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить доску'))
    }
    return sortBoardsByPosition((res.data?.data ?? []).map(mapBoardFromApi))
  },

  async moveBoards(
    projectId: number,
    items: MoveTaskTrackerBoardsItem[],
  ): Promise<TaskTrackerBoard[] | null> {
    const res = await API.post<ResponseWrapper<ApiBoardsMoveResponse>>(
      `task-tracker-boards/move${getParams({ project_id: projectId })}`,
      { items },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось изменить порядок досок'))
    }

    const boards = res.data?.data?.boards
    return Array.isArray(boards)
      ? sortBoardsByPosition(boards.map(mapBoardFromApi))
      : null
  },

  async createBoard(
    projectId: number,
    payload: CreateTaskTrackerBoardRequest,
  ): Promise<TaskTrackerBoard> {
    const body: {
      title: string
      stickers?: CreateTaskTrackerBoardRequest['stickers']
    } = {
      title: payload.title.trim(),
    }

    if (payload.stickers?.length) {
      body.stickers = payload.stickers
    }

    const res = await API.post<ResponseWrapper<ApiBoard>>(
      `task-tracker-boards/create${getParams({ project_id: projectId })}`,
      body,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось создать доску'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось создать доску')
    }
    return mapBoardFromApi(res.data.data)
  },

  async updateBoard(
    boardId: number,
    payload: UpdateTaskTrackerBoardRequest,
  ): Promise<TaskTrackerBoard> {
    const res = await API.post<ResponseWrapper<ApiBoard>>(
      `task-tracker-boards/update${getParams({ board_id: boardId })}`,
      { title: payload.title.trim() },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить название доски'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить название доски')
    }
    return mapBoardFromApi(res.data.data)
  },

  async deleteBoard(
    boardId: number,
    confirmation: string,
  ): Promise<{ deleted: boolean; boardId: number; projectId: number }> {
    const res = await API.delete<ResponseWrapper<ApiDeleteBoardResponse>>(
      `task-tracker-boards/delete${getParams({ board_id: boardId })}`,
      {
        ...TASK_TRACKER_API_REQUEST_CONFIG,
        data: { confirmation },
      },
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить доску'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось удалить доску')
    }

    return {
      deleted: Boolean(res.data.data.deleted),
      boardId: Number(res.data.data.board_id ?? boardId),
      projectId: Number(res.data.data.project_id),
    }
  },

  async getBoardAccess(boardId: number): Promise<TaskTrackerBoardAccessSettings> {
    const res = await API.get<ResponseWrapper<ApiBoardAccessResponse>>(
      `task-tracker-boards/access${getParams({ board_id: boardId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось получить настройки доступа доски'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось получить настройки доступа доски')
    }
    return mapBoardAccessFromApi(res.data.data)
  },

  async updateBoardAccess(
    boardId: number,
    payload: UpdateTaskTrackerBoardAccessRequest,
  ): Promise<TaskTrackerBoardAccessSettings> {
    const body =
      payload.accessMode === 'all_project_members'
        ? { access_mode: 'all_project_members' as const }
        : {
            access_mode: 'selected_members' as const,
            user_ids: payload.userIds ?? [],
          }

    const res = await API.post<ResponseWrapper<ApiBoardAccessResponse>>(
      `task-tracker-boards/access-update${getParams({ board_id: boardId })}`,
      body,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось обновить настройки доступа доски'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось обновить настройки доступа доски')
    }
    return mapBoardAccessFromApi(res.data.data)
  },

  async refreshBoardItems(
    params: GetTaskTrackerBoardRefreshItemsParams,
  ): Promise<TaskTrackerBoardRefreshItemsResult> {
    const res = await API.get<ResponseWrapper<ApiBoardRefreshItemsResponse>>(
      `task-tracker-boards/refresh-items${buildBoardRefreshItemsQuery(params)}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось обновить колонки и сводки доски'),
      )
    }

    const data = res.data?.data
    if (!data) {
      throw new Error('Не удалось обновить колонки и сводки доски')
    }

    return {
      columns: (data.columns ?? []).map((item) => ({
        columnId: Number(item.column_id),
        items: mapBoardCardsFromApi(item.items),
        nextCursor: normalizeRefreshCursor(item.next_cursor),
        hasMore: Boolean(item.has_more),
        totalCount: Number(item.total_count ?? 0),
        limit: normalizeRefreshLimit(item.limit),
        sortBy: item.sort_by?.trim() ? String(item.sort_by).trim() : null,
        sortOrder: item.sort_order === 'desc' ? 'desc' : 'asc',
      })),
      summaries: (data.summaries ?? []).map((item) => ({
        summaryId: Number(item.summary_id),
        items: mapBoardCardsFromApi(item.items),
        nextCursor: normalizeRefreshCursor(item.next_cursor),
        hasMore: Boolean(item.has_more),
        totalCount: Number(item.total_count ?? 0),
      })),
    }
  },
}
